/**
 * Native wallet service — zero WalletConnect dependency.
 * Connects to Metamask, Rabby, D'CENT, and any injected EIP-1193 wallet.
 * Ledger hardware wallet is accessed via its own HID transport.
 */
import { BrowserProvider, formatEther, type Eip1193Provider } from 'ethers'
import { getPreferredProvider, detectInjectedWallets, type EthereumProvider } from './walletDetect'
import { getChain, chainToHex, isSupported } from '@/lib/chains/chainConfig'
import { useWalletStore } from '@/stores/walletStore'
import type { WalletType, ChainId } from '@/types'

// ─── Debug logger ────────────────────────────────────────────────────────────
// All output goes to the browser console under the [WalletService] prefix.
// Disable by setting window.__WALLET_DEBUG = false in the console.

function dbg(method: string, ...args: unknown[]) {
  if ((window as unknown as Record<string, unknown>).__WALLET_DEBUG === false) return
  console.log(
    `%c[WalletService.${method}]`,
    'color:#E8850C;font-weight:bold',
    ...args,
  )
}

function dbgErr(method: string, ...args: unknown[]) {
  console.error(`[WalletService.${method}]`, ...args)
}

// The platform's preferred entry chain. When a wallet lands on Ethereum
// mainnet (id 1) — which it does by default in every major injected
// extension — we steer it to XDC so Checkout/ChainPicker don't start on a
// chain we don't want to transact on. Users can still manually switch
// to any supported chain afterwards.
const DEFAULT_CHAIN = 50 as const
const ETHEREUM_MAINNET_ID = 1

class WalletService {
  private _provider: EthereumProvider | null = null
  private _accountsHandler?: (...args: unknown[]) => void
  private _chainHandler?: (...args: unknown[]) => void

  get provider(): EthereumProvider | null {
    // Lazy rebind on reads — after a page reload the class instance is fresh
    // but window.ethereum is still injected. Without this, switchChain and
    // signMessage fail with "Wallet not connected" until the user reconnects.
    if (!this._provider) this.tryRebind()
    return this._provider
  }

  get ethersProvider(): BrowserProvider | null {
    const p = this.provider
    if (!p) return null
    return new BrowserProvider(p as Eip1193Provider)
  }

  detectWallets() {
    return detectInjectedWallets()
  }

  /**
   * Re-attach to whichever injected provider matches the persisted wallet type.
   * Safe to call multiple times — no-op if a provider is already bound.
   * Returns true when a provider is now bound.
   *
   * Also silently syncs the current account and chain from the extension
   * (without triggering a popup) so the store reflects reality after a reload.
   */
  tryRebind(preferredType?: WalletType): boolean {
    if (this._provider) return true
    const raw = getPreferredProvider(preferredType)
    if (!raw) return false
    dbg('tryRebind', 'rebound to injected provider', {
      isMetaMask: raw.isMetaMask,
      isRabby: raw.isRabby,
      isCoinbaseWallet: raw.isCoinbaseWallet,
    })
    this._provider = raw
    this._setupListeners(raw)

    // Silent sync — eth_accounts does NOT trigger a popup, only eth_requestAccounts does.
    void (async () => {
      try {
        const accounts = (await raw.request({ method: 'eth_accounts' })) as string[]
        if (!accounts?.length) {
          // The user disconnected this site from the wallet extension while we
          // were closed — clear the persisted state.
          dbg('tryRebind', 'no accounts in extension, clearing store')
          useWalletStore.getState().disconnect()
          return
        }
        const persistedAddr = useWalletStore.getState().address
        if (persistedAddr && persistedAddr.toLowerCase() !== accounts[0].toLowerCase()) {
          dbg('tryRebind', 'account changed since last visit', {
            was: persistedAddr,
            now: accounts[0],
          })
          useWalletStore.getState().setConnected(
            accounts[0],
            useWalletStore.getState().chainId ?? (1 as ChainId),
            useWalletStore.getState().walletType ?? 'injected'
          )
        }
        const chainHex = (await raw.request({ method: 'eth_chainId' })) as string
        const chain = parseInt(chainHex, 16) as ChainId
        if (chain && useWalletStore.getState().chainId !== chain) {
          useWalletStore.getState().setChain(chain)
        }
        if (chain === ETHEREUM_MAINNET_ID) {
          const addr = useWalletStore.getState().address ?? accounts[0]
          const wType = useWalletStore.getState().walletType ?? 'injected'
          await this._steerToDefaultChain(addr, wType)
        }
      } catch (err) {
        dbg('tryRebind', 'silent sync failed (non-fatal):', err)
      }
    })()

    return true
  }

  async connect(type?: WalletType): Promise<{ address: string; chainId: ChainId }> {
    dbg('connect', 'type:', type)
    const raw = getPreferredProvider(type)
    if (!raw) throw new Error('No injected wallet detected. Please install MetaMask or Rabby.')

    dbg('connect', 'provider found:', {
      isMetaMask: raw.isMetaMask,
      isRabby: raw.isRabby,
      isDCent: raw.isDCent,
      isCoinbaseWallet: raw.isCoinbaseWallet,
      isBraveWallet: raw.isBraveWallet,
    })

    this._provider = raw

    // Request accounts (triggers wallet popup)
    const accounts = (await raw.request({ method: 'eth_requestAccounts' })) as string[]
    if (!accounts.length) throw new Error('No accounts returned')
    const address = accounts[0]
    dbg('connect', 'account:', address)

    // Get chain
    const chainIdHex = (await raw.request({ method: 'eth_chainId' })) as string
    let chainId = parseInt(chainIdHex, 16) as ChainId
    dbg('connect', 'current chain:', chainId, `(${chainIdHex})`)

    // Subscribe to events before any switch attempt so chainChanged reliably
    // updates the store even if the user approves the switch mid-connect.
    this._setupListeners(raw)

    // Update store
    const walletType: WalletType = type ?? 'injected'
    useWalletStore.getState().setConnected(address, chainId, walletType)

    // Steer off Ethereum mainnet. Requires a wallet approval popup, but we'd
    // rather face that once at connect time than run the app on ETH.
    if (chainId === ETHEREUM_MAINNET_ID) {
      chainId = await this._steerToDefaultChain(address, walletType) ?? chainId
    }

    // Balance is fetched by the store's connect() flow — avoid duplicate fetch here.

    return { address, chainId }
  }

  /**
   * Attempt to move the wallet to DEFAULT_CHAIN. Swallows rejection — if the
   * user declines the prompt we leave them on whatever chain they were on
   * rather than failing the entire connect flow. Returns the final chainId
   * (DEFAULT_CHAIN on success, undefined if unchanged).
   */
  private async _steerToDefaultChain(
    address: string,
    walletType: WalletType,
  ): Promise<ChainId | undefined> {
    dbg('_steerToDefaultChain', `wallet on ETH (1) — steering to ${DEFAULT_CHAIN}`)
    try {
      await this.switchChain(DEFAULT_CHAIN)
      useWalletStore.getState().setConnected(address, DEFAULT_CHAIN, walletType)
      return DEFAULT_CHAIN
    } catch (err) {
      dbg('_steerToDefaultChain', 'switch declined or failed — staying on current chain', err)
      return undefined
    }
  }

  async switchChain(chainId: number): Promise<void> {
    dbg('switchChain', '── START ──  target:', chainId)

    // Attempt lazy rebind first — handles the post-reload case where the
    // store is rehydrated as connected but this class instance is fresh.
    if (!this._provider) {
      const persistedType = useWalletStore.getState().walletType ?? undefined
      this.tryRebind(persistedType ?? undefined)
    }

    if (!this._provider) {
      dbgErr('switchChain', 'ABORT: no provider — wallet not connected')
      throw new Error('Wallet not connected')
    }

    // Log current wallet state
    try {
      const curHex = (await this._provider.request({ method: 'eth_chainId' })) as string
      dbg('switchChain', 'wallet currently on chain:', parseInt(curHex, 16), `(${curHex})`)
    } catch (e) {
      dbg('switchChain', 'could not read current chain:', e)
    }

    const hexId = chainToHex(chainId)
    const chain = getChain(chainId)
    dbg('switchChain', 'target hex:', hexId, '| chain config:', chain ? {
      name: chain.name,
      rpcUrls: chain.rpcUrls,
      blockExplorerUrl: chain.blockExplorerUrl,
      nativeCurrency: chain.nativeCurrency,
    } : 'NOT FOUND in CHAINS map')

    if (!chain) {
      dbgErr('switchChain', `ABORT: chainId ${chainId} not in CHAINS config`)
      throw new Error(`Unsupported chain: ${chainId}`)
    }

    // ── Step 1: Try wallet_switchEthereumChain ──────────────────────────────
    dbg('switchChain', '→ calling wallet_switchEthereumChain', { chainId: hexId })
    try {
      const switchResult = await this._provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexId }],
      })
      dbg('switchChain', '✓ wallet_switchEthereumChain succeeded, result:', switchResult)
      useWalletStore.getState().setChain(chainId as ChainId)
      dbg('switchChain', '── DONE (switch path) ──')
      return
    } catch (err: unknown) {
      const code = (err as { code?: number }).code
      const message = (err as { message?: string }).message
      dbg('switchChain', '✗ wallet_switchEthereumChain failed:', {
        code,
        message,
        errorType: typeof err,
        fullError: err,
      })

      // User rejected — bail immediately
      if (code === 4001) {
        dbg('switchChain', 'user rejected (4001) — aborting')
        throw err
      }

      // ── Step 2: Try wallet_addEthereumChain ────────────────────────────────
      const addParams = {
        chainId: hexId,
        chainName: chain.name,
        nativeCurrency: chain.nativeCurrency,
        rpcUrls: chain.rpcUrls,
        blockExplorerUrls: chain.blockExplorerUrl
          ? [chain.blockExplorerUrl]
          : undefined,
      }
      dbg('switchChain', '→ calling wallet_addEthereumChain with:', JSON.parse(JSON.stringify(addParams)))

      try {
        const addResult = await this._provider.request({
          method: 'wallet_addEthereumChain',
          params: [addParams],
        })
        dbg('switchChain', '✓ wallet_addEthereumChain succeeded, result:', addResult)
      } catch (addErr: unknown) {
        const addCode = (addErr as { code?: number }).code
        const addMsg = (addErr as { message?: string }).message
        dbgErr('switchChain', '✗ wallet_addEthereumChain ALSO failed:', {
          code: addCode,
          message: addMsg,
          fullError: addErr,
        })
        throw addErr
      }

      // ── Step 3: Verify we actually switched ────────────────────────────────
      try {
        const currentHex = (await this._provider.request({ method: 'eth_chainId' })) as string
        const currentChain = parseInt(currentHex, 16)
        dbg('switchChain', 'post-add chain check:', currentChain, `(${currentHex})`, currentChain === chainId ? '✓ correct' : '✗ MISMATCH — will retry switch')

        if (currentChain !== chainId) {
          dbg('switchChain', '→ retrying wallet_switchEthereumChain after add')
          await this._provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: hexId }],
          })
          dbg('switchChain', '✓ retry switch succeeded')
        }
      } catch (verifyErr: unknown) {
        dbgErr('switchChain', 'post-add verification/retry failed:', verifyErr)
        throw verifyErr
      }
    }

    useWalletStore.getState().setChain(chainId as ChainId)
    dbg('switchChain', '── DONE (add+switch path) ──')
  }

  /**
   * Adopt a pre-selected EIP-1193 provider from the provider detection system.
   * Call this after the user selects a wallet in WalletModal so that sign/switch
   * operations work against the correct provider.
   */
  setProvider(rawProvider: EthereumProvider): void {
    dbg('setProvider', 'swapping provider', {
      isMetaMask: rawProvider.isMetaMask,
      isRabby: rawProvider.isRabby,
    })
    if (this._provider) {
      if (this._accountsHandler)
        this._provider.removeListener('accountsChanged', this._accountsHandler)
      if (this._chainHandler)
        this._provider.removeListener('chainChanged', this._chainHandler)
    }
    this._provider = rawProvider
    this._setupListeners(rawProvider)
  }

  async disconnect(): Promise<void> {
    dbg('disconnect', 'disconnecting')
    if (this._provider) {
      if (this._accountsHandler)
        this._provider.removeListener('accountsChanged', this._accountsHandler)
      if (this._chainHandler)
        this._provider.removeListener('chainChanged', this._chainHandler)
    }
    this._provider = null
    useWalletStore.getState().disconnect()
  }

  async signMessage(message: string): Promise<string> {
    // The `ethersProvider` getter lazy-rebinds via `provider`, which covers the
    // post-reload case where the store is rehydrated but this class instance
    // is fresh.
    const ep = this.ethersProvider
    if (!ep) throw new Error('Wallet not connected')
    const signer = await ep.getSigner()
    return signer.signMessage(message)
  }

  async getBalance(address?: string): Promise<string> {
    const ep = this.ethersProvider
    if (!ep) throw new Error('Wallet not connected')
    const addr = address ?? useWalletStore.getState().address
    if (!addr) throw new Error('No address')
    const balance = await ep.getBalance(addr)
    return formatEther(balance)
  }

  private _setupListeners(raw: EthereumProvider): void {
    this._accountsHandler = (...args: unknown[]) => {
      const accounts = args[0] as string[]
      dbg('event:accountsChanged', accounts)
      if (!accounts || accounts.length === 0) {
        this.disconnect()
      } else {
        useWalletStore.getState().setConnected(
          accounts[0],
          useWalletStore.getState().chainId!,
          useWalletStore.getState().walletType!
        )
      }
    }

    this._chainHandler = (...args: unknown[]) => {
      const chainIdHex = args[0] as string
      const chainId = parseInt(chainIdHex, 16) as ChainId
      dbg('event:chainChanged', chainId, `(${chainIdHex})`, isSupported(Number(chainId)) ? '(supported)' : '(UNSUPPORTED)')
      useWalletStore.getState().setChain(chainId)
      if (!isSupported(Number(chainId))) {
        console.warn(`Switched to unsupported chain: ${chainId}`)
      }
    }

    raw.on('accountsChanged', this._accountsHandler)
    raw.on('chainChanged', this._chainHandler)
  }
}

export const walletService = new WalletService()
